import React from 'react';
import styles from './EnhancedComparisonResult.module.scss';

interface ResultSection {
  name?: string;
  status?:
    | 'compliant'
    | 'non-compliant'
    | 'missing'
    | 'needs-improvement'
    | 'partial';
  score?: number;
  content?: string;
  // Optional fields from API for richer rendering
  explanation?: string;
  suggestedText?: string;
  findings?: string[];
  recommendations?: string[];
}

interface ResultShape {
  customerDocument: { name: string; wordCount?: number };
  analysis?: {
    overallScore?: number;
    riskLevel?: 'low' | 'medium' | 'high' | 'critical';
  };
  comparisonSummary?: {
    compliantSections?: number;
    nonCompliantSections?: number;
    missingSections?: number;
  };
  findings?: Array<{
    clause: string;
    status: 'match' | 'non-compliant' | 'missing';
    notes?: string;
    suggestedText?: string;
  }>;
  sectionAnalysis?: ResultSection[];
}

interface EnhancedComparisonResultProps {
  result: ResultShape;
  analysisMethod: 'manual' | 'llm';
  onClose: () => void;
  onStartNewReview: () => void;
}

const getRiskColor = (risk: string): string => {
  switch (risk) {
    case 'low':
      return '#28a745';
    case 'medium':
      return '#ffc107';
    case 'high':
      return '#fd7e14';
    case 'critical':
      return '#dc3545';
    default:
      return '#6c757d';
  }
};

const getScoreColor = (score: number): string => {
  if (score >= 75) return '#28a745';
  if (score >= 60) return '#ffc107';
  if (score >= 40) return '#fd7e14';
  return '#dc3545';
};

export const EnhancedComparisonResult: React.FC<
  EnhancedComparisonResultProps
> = ({ result, analysisMethod, onClose, onStartNewReview }) => {
  console.log('EnhancedComparisonResult rendered', result);
  const riskLevel = result.analysis?.riskLevel;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h2 className={styles.title}>NDA Analysis Results</h2>
          <p className={styles.documentName}>
            Document: {result.customerDocument.name}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button onClick={onStartNewReview} className={styles.newReviewButton}>
            Start New Review
          </button>
          <button onClick={onClose} className={styles.closeButton}>
            ✕
          </button>
        </div>
      </div>

      {riskLevel && (
        <div className={styles.overallSection}>
          <div className={styles.riskCard}>
            <div
              className={styles.riskIndicator}
              style={{ backgroundColor: getRiskColor(riskLevel) }}
            >
              {riskLevel.toUpperCase()}
            </div>
            <div className={styles.riskInfo}>
              <h3>Risk Level</h3>
              <p>Based on compliance gaps and legal requirements</p>
            </div>
          </div>
        </div>
      )}

      <div className={styles.statsSection}>
        {result.comparisonSummary?.compliantSections !== undefined && (
          <div className={styles.statCard}>
            <div className={styles.statNumber}>
              {result.comparisonSummary.compliantSections}
            </div>
            <div className={styles.statLabel}>Compliant Sections</div>
          </div>
        )}
        {result.comparisonSummary?.nonCompliantSections !== undefined && (
          <div className={styles.statCard}>
            <div className={styles.statNumber}>
              {result.comparisonSummary.nonCompliantSections}
            </div>
            <div className={styles.statLabel}>Non-Compliant</div>
          </div>
        )}
        {result.comparisonSummary?.missingSections !== undefined && (
          <div className={styles.statCard}>
            <div className={styles.statNumber}>
              {result.comparisonSummary.missingSections}
            </div>
            <div className={styles.statLabel}>Missing Sections</div>
          </div>
        )}
        {result.customerDocument.wordCount !== undefined && (
          <div className={styles.statCard}>
            <div className={styles.statNumber}>
              {result.customerDocument.wordCount}
            </div>
            <div className={styles.statLabel}>Word Count</div>
          </div>
        )}
      </div>

      <div className={styles.sectionsContainer}>
        <h3 className={styles.sectionsTitle}>Section Analysis</h3>
        <div className={styles.sectionsGrid}>
          {result.sectionAnalysis?.map(
            (section: ResultSection, index: number) => {
              const {
                name,
                status,
                score,
                content,
                explanation,
                suggestedText,
                recommendations
              } = section ?? {};
              return (
                <div
                  key={`${name ?? 'section'}-${status ?? 'unknown'}-${index}`}
                  className={styles.sectionCard}
                >
                  <div className={styles.sectionHeader}>
                    {name && <h4 className={styles.sectionName}>{name}</h4>}
                    {status && (
                      <div
                        className={`${styles.statusBadge} ${styles[status]}`}
                      >
                        {status.replace('-', ' ').toUpperCase()}
                      </div>
                    )}
                  </div>
                  {typeof score === 'number' && (
                    <div className={styles.sectionScore}>
                      <div className={styles.scoreBar}>
                        <div
                          className={styles.scoreBarFill}
                          style={{
                            width: `${score}%`,
                            backgroundColor: getScoreColor(score)
                          }}
                        />
                      </div>
                      <span className={styles.scoreValue}>{score}%</span>
                    </div>
                  )}
                  {content && (
                    <p className={styles.sectionContent}>{content}</p>
                  )}
                  {explanation && (
                    <p className={styles.sectionContent}>{explanation}</p>
                  )}
                  {Array.isArray(recommendations) &&
                    recommendations.length > 0 && (
                      <div className={styles.sectionRecommendations}>
                        <strong>Recommendations:</strong>
                        <ul>
                          {recommendations.map((rec) => (
                            <li key={`rec-${String(name ?? 'section')}-${rec}`}>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {suggestedText && (
                    <div className={styles.sectionRecommendations}>
                      <strong>Suggested Text:</strong>
                      <p className={styles.sectionContent}>{suggestedText}</p>
                    </div>
                  )}
                </div>
              );
            }
          )}
        </div>
      </div>

      {Array.isArray(result.findings) && result.findings.length > 0 && (
        <div className={styles.sectionsContainer}>
          <h3 className={styles.sectionsTitle}>Findings</h3>
          <div className={styles.sectionsGrid}>
            {result.findings.map((item, i) => {
              const isNonCompliant = item.status === 'non-compliant';
              return (
                <div
                  key={`${item.clause}-${item.status}-${i}`}
                  className={styles.sectionCard}
                >
                  <div className={styles.sectionHeader}>
                    <h4 className={styles.sectionName}>{item.clause}</h4>
                    <div
                      className={`${styles.statusBadge} ${styles[item.status]}`}
                    >
                      {String(item.status).replace('-', ' ').toUpperCase()}
                    </div>
                  </div>
                  {item.notes && (
                    <p className={styles.sectionContent}>{item.notes}</p>
                  )}
                  {isNonCompliant && item.suggestedText && (
                    <div className={styles.sectionRecommendations}>
                      <strong>Suggested Text:</strong>
                      <p className={styles.sectionContent}>
                        {item.suggestedText}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedComparisonResult;
